import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Users,
  Key,
  Building2,
  Pencil, 
  Trash2,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Copy,
  Hash,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import api, { clientsAPI, propertiesAPI, tenantAccountsAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';

// Generate random password
const generatePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let password = '';
  for (let i = 0; i < 8; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

// Generate username from client name
const generateUsername = (clientName, clientCode) => {
  if (clientCode) {
    return clientCode.toLowerCase().replace(/[^a-z0-9]/g, '');
  }
  const parts = clientName.toLowerCase().split(' ');
  if (parts.length >= 2) {
    return parts[0].charAt(0) + parts[1].substring(0, 7);
  }
  return parts[0].substring(0, 8);
};

const TenantAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCredentialsOpen, setIsCredentialsOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    client_id: '',
    property_id: '',
  });
  
  // Credentials to show after creation
  const [newCredentials, setNewCredentials] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [accountsRes, clientsRes, propertiesRes] = await Promise.all([
        api.get('/tenant-accounts'),
        clientsAPI.getAll(),
        propertiesAPI.getAll()
      ]);
      setAccounts(accountsRes.data);
      // Filter only tenants (locataires)
      setClients(clientsRes.data.filter(c => c.client_type === 'locataire'));
      // Filter only rental properties
      setProperties(propertiesRes.data.filter(p => p.transaction_type === 'location'));
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (account = null) => {
    if (account) {
      setSelectedAccount(account);
      setFormData({
        username: account.username,
        password: '',
        client_id: account.client_id,
        property_id: account.property_id,
      });
    } else {
      setSelectedAccount(null);
      setFormData({
        username: '',
        password: generatePassword(),
        client_id: '',
        property_id: '',
      });
    }
    setShowPassword(false);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedAccount(null);
    setFormData({
      username: '',
      password: '',
      client_id: '',
      property_id: '',
    });
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setFormData({
        ...formData,
        client_id: clientId,
        username: formData.username || generateUsername(client.name, client.client_code),
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.client_id || !formData.property_id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (!selectedAccount && !formData.password) {
      toast.error('Veuillez définir un mot de passe');
      return;
    }

    setSaving(true);
    try {
      if (selectedAccount) {
        // Update
        const updateData = {
          username: formData.username,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await api.put(`/tenant-accounts/${selectedAccount.id}`, updateData);
        toast.success('Compte locataire mis à jour');
        handleCloseForm();
      } else {
        // Create
        const response = await api.post('/tenant-accounts', formData);
        
        // Show credentials
        const client = clients.find(c => c.id === formData.client_id);
        setNewCredentials({
          username: formData.username,
          password: formData.password,
          clientName: client?.name || '',
        });
        setIsCredentialsOpen(true);
        
        handleCloseForm();
        toast.success('Compte locataire créé avec succès');
      }
      
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (account) => {
    try {
      await api.put(`/tenant-accounts/${account.id}`, {
        is_active: !account.is_active
      });
      toast.success(account.is_active ? 'Compte désactivé' : 'Compte activé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDelete = async () => {
    if (!selectedAccount) return;
    
    try {
      await api.delete(`/tenant-accounts/${selectedAccount.id}`);
      toast.success('Compte locataire supprimé');
      setIsDeleteOpen(false);
      setSelectedAccount(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleResetPassword = async (account) => {
    const newPassword = generatePassword();
    try {
      await api.put(`/tenant-accounts/${account.id}`, {
        password: newPassword
      });
      
      const client = clients.find(c => c.id === account.client_id);
      setNewCredentials({
        username: account.username,
        password: newPassword,
        clientName: client?.name || account.client_name || '',
      });
      setIsCredentialsOpen(true);
      
      toast.success('Mot de passe réinitialisé');
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const handleGeneratePin = async (account) => {
    try {
      const res = await tenantAccountsAPI.generatePin(account.id);
      const client = clients.find(c => c.id === account.client_id);
      setNewCredentials({
        username: res.data.username,
        pin_code: res.data.pin_code,
        clientName: client?.name || account.client_name || '',
      });
      setIsCredentialsOpen(true);
      toast.success('Code PIN à 4 chiffres généré');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération du code PIN');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papiers');
  };

  const filteredAccounts = accounts.filter(account => {
    const searchLower = searchQuery.toLowerCase();
    return (
      account.username?.toLowerCase().includes(searchLower) ||
      account.client_name?.toLowerCase().includes(searchLower) ||
      account.property_title?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenant-accounts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Accès Locataires</h1>
          <p className="text-gray-500">{accounts.length} compte(s) créé(s)</p>
        </div>
        <Button 
          onClick={() => handleOpenForm()}
          className="bg-[#1B3A4B] hover:bg-[#142D3A]"
          data-testid="add-tenant-account-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Créer un accès
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher par identifiant, locataire ou bien..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-tenant-accounts"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Identifiant</TableHead>
                <TableHead>Locataire</TableHead>
                <TableHead>Bien</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Dernière connexion</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAccounts.length > 0 ? (
                filteredAccounts.map((account) => (
                  <TableRow key={account.id} data-testid={`account-row-${account.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-[#D4A373]" />
                        <span className="font-mono font-medium">{account.username}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{account.client_name}</p>
                        <p className="text-sm text-gray-500">{account.client_phone}</p>
                        {account.client_code && (
                          <span className="text-xs font-mono text-[#1B3A4B]">{account.client_code}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{account.property_title}</p>
                        {account.property_code && (
                          <span className="text-xs font-mono text-[#1B3A4B]">{account.property_code}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        className={account.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                        }
                      >
                        {account.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {account.last_login 
                        ? formatDate(account.last_login)
                        : <span className="text-gray-400">Jamais</span>
                      }
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResetPassword(account)}
                          title="Réinitialiser le mot de passe"
                          data-testid={`reset-password-btn-${account.id}`}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleGeneratePin(account)}
                          title="Générer un code PIN à 4 chiffres"
                          className="text-[#1B3A4B] hover:text-[#D4A373]"
                          data-testid={`generate-pin-btn-${account.id}`}
                        >
                          <Hash className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(account)}
                          title={account.is_active ? 'Désactiver' : 'Activer'}
                        >
                          {account.is_active ? (
                            <XCircle className="w-4 h-4 text-red-500" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenForm(account)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedAccount(account);
                            setIsDeleteOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">
                      {searchQuery ? 'Aucun compte trouvé' : 'Aucun compte locataire créé'}
                    </p>
                    {!searchQuery && (
                      <Button
                        variant="link"
                        onClick={() => handleOpenForm()}
                        className="mt-2"
                      >
                        Créer le premier accès
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedAccount ? 'Modifier le compte' : 'Créer un accès locataire'}
            </DialogTitle>
            <DialogDescription>
              {selectedAccount 
                ? 'Modifiez les informations du compte locataire'
                : 'Créez un compte pour permettre au locataire d\'accéder à son espace'
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {!selectedAccount && (
              <>
                <div>
                  <Label htmlFor="client_id">Locataire *</Label>
                  <Select
                    value={formData.client_id}
                    onValueChange={handleClientSelect}
                  >
                    <SelectTrigger data-testid="select-client">
                      <SelectValue placeholder="Sélectionner un locataire" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name} {client.client_code && `(${client.client_code})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {clients.length === 0 && (
                    <p className="text-xs text-orange-600 mt-1">
                      Aucun locataire trouvé. Créez d'abord un client de type "locataire".
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="property_id">Bien loué *</Label>
                  <Select
                    value={formData.property_id}
                    onValueChange={(value) => setFormData({ ...formData, property_id: value })}
                  >
                    <SelectTrigger data-testid="select-property">
                      <SelectValue placeholder="Sélectionner un bien" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map((prop) => (
                        <SelectItem key={prop.id} value={prop.id}>
                          {prop.title} {prop.property_code && `(${prop.property_code})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {properties.length === 0 && (
                    <p className="text-xs text-orange-600 mt-1">
                      Aucun bien en location trouvé.
                    </p>
                  )}
                </div>
              </>
            )}

            <div>
              <Label htmlFor="username">Identifiant de connexion *</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Ex: amadou.diallo"
                data-testid="username-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Identifiant unique que le locataire utilisera pour se connecter
              </p>
            </div>

            <div>
              <Label htmlFor="password">
                {selectedAccount ? 'Nouveau mot de passe' : 'Mot de passe *'}
              </Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={selectedAccount ? "Laisser vide pour ne pas modifier" : "Mot de passe"}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormData({ ...formData, password: generatePassword() })}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                data-testid="submit-account-btn"
              >
                {saving ? 'Enregistrement...' : selectedAccount ? 'Modifier' : 'Créer le compte'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials Display Dialog */}
      <Dialog open={isCredentialsOpen} onOpenChange={setIsCredentialsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Identifiants créés
            </DialogTitle>
            <DialogDescription>
              Communiquez ces identifiants au locataire. Le mot de passe ne sera plus visible après cette étape.
            </DialogDescription>
          </DialogHeader>
          
          {newCredentials && (
            <div className="space-y-4">
              <Card className="bg-gray-50">
                <CardContent className="p-4 space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Locataire</p>
                    <p className="font-medium">{newCredentials.clientName}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Identifiant</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white px-3 py-2 rounded border font-mono">
                        {newCredentials.username}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(newCredentials.username)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {newCredentials.password && (
                    <div>
                      <p className="text-sm text-gray-500">Mot de passe</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-white px-3 py-2 rounded border font-mono">
                          {newCredentials.password}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(newCredentials.password)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {newCredentials.pin_code && (
                    <div>
                      <p className="text-sm text-gray-500">Code PIN (4 chiffres)</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-white px-3 py-2 rounded border font-mono text-2xl text-center tracking-[0.5em] text-[#1B3A4B]" data-testid="generated-pin-code">
                          {newCredentials.pin_code}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(newCredentials.pin_code)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-orange-700 mt-1">
                        Ce code ne sera plus affiché. Notez-le ou partagez-le tout de suite.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                <p className="font-medium mb-1">Instructions pour le locataire:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Aller sur l'espace locataire</li>
                  {newCredentials.pin_code ? (
                    <>
                      <li>Sélectionner l'onglet "Code PIN"</li>
                      <li>Entrer l'identifiant et le code à 4 chiffres</li>
                    </>
                  ) : (
                    <>
                      <li>Sélectionner "Connexion avec identifiant"</li>
                      <li>Entrer l'identifiant et le mot de passe</li>
                    </>
                  )}
                </ol>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (newCredentials) {
                  const secret = newCredentials.pin_code
                    ? `Code PIN: ${newCredentials.pin_code}`
                    : `Mot de passe: ${newCredentials.password}`;
                  const text = `Identifiant: ${newCredentials.username}\n${secret}`;
                  copyToClipboard(text);
                }
              }}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copier tout
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (!newCredentials) return;
                const secret = newCredentials.pin_code
                  ? `Code PIN: *${newCredentials.pin_code}*`
                  : `Mot de passe: *${newCredentials.password}*`;
                const msg = encodeURIComponent(
                  `Bonjour ${newCredentials.clientName},\n\nVoici vos identifiants pour l'Espace Locataire :\n\nIdentifiant: *${newCredentials.username}*\n${secret}\n\nConnectez-vous sur l'espace locataire de votre agence.`
                );
                window.open(`https://wa.me/?text=${msg}`, '_blank');
              }}
              className="border-green-500 text-green-700 hover:bg-green-50"
              data-testid="share-whatsapp-btn"
            >
              Partager WhatsApp
            </Button>
            <Button
              onClick={() => setIsCredentialsOpen(false)}
              className="bg-[#1B3A4B] hover:bg-[#142D3A]"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce compte ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le locataire "{selectedAccount?.client_name}" ne pourra plus accéder à son espace.
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TenantAccounts;
